import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  CheckCircle2,
  Clock,
  FileText,
  Users,
  TrendingUp,
  ArrowRight,
  Upload,
  Library,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import { Skeleton, SkeletonRow } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { ImportQuestionsModal } from '@/components/ImportQuestionsModal';
import { useAuth } from '@/store/auth';
import { api } from '@/lib/api';
import { useT } from '@/store/i18n';
import type { Question } from '@/types';

interface DashboardStats {
  questions_by_status?: {
    approved?: number;
    pending?: number;
    draft?: number;
    rejected?: number;
  };
  total_questions?: number;
  total_users?: number;
  my_questions_count?: number;
}

function useGreeting(name?: string) {
  const t = useT();
  const h = new Date().getHours();
  const part = h < 12 ? t('greet.morning') : h < 17 ? t('greet.afternoon') : t('greet.evening');
  return `${part}${name ? `, ${name}` : ''}`;
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  tone?: 'brand' | 'amber' | 'blue' | 'red' | 'violet';
  loading?: boolean;
}

const TONES = {
  brand: 'bg-brand-50 text-brand-600',
  amber: 'bg-amber-50 text-amber-600',
  blue: 'bg-blue-50 text-blue-600',
  red: 'bg-red-50 text-red-600',
  violet: 'bg-violet-50 text-violet-600',
};

function StatCard({ icon, label, value, tone = 'brand', loading }: StatCardProps) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-4">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${TONES[tone]}`}>
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          {loading ? (
            <Skeleton className="w-16 h-7 mb-1" />
          ) : (
            <div className="text-2xl font-bold text-slate-900 leading-none">
              {value}
            </div>
          )}
          <div className="text-xs text-slate-500 mt-1.5">{label}</div>
        </div>
      </div>
    </Card>
  );
}

export function Dashboard() {
  const t = useT();
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const role = user?.role;
  const greeting = useGreeting(user?.full_name?.split(' ')[0]);
  const [importOpen, setImportOpen] = useState(false);

  const stats = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () =>
      api<{ data?: DashboardStats } & DashboardStats>('dashboard/stats.php').then(
        (r) => r.data ?? r
      ),
  });

  const recent = useQuery({
    queryKey: ['recent-questions', role],
    queryFn: () =>
      api<{ data?: { questions?: Question[] } } & { questions?: Question[] }>(
        `questions/index.php?limit=6&sort=created_at&order=desc${
          role === 'teacher' ? '&status=approved' : ''
        }`
      ).then((r) => r.data?.questions ?? r.questions ?? []),
  });

  const s = stats.data ?? {};
  const byStatus = s.questions_by_status ?? {};
  const isNewUser =
    !stats.isLoading &&
    (s.my_questions_count === 0 || s.my_questions_count === undefined) &&
    (s.total_questions === 0 || s.total_questions === undefined);
  const canCreate = role === 'teacher' || role === 'academic_admin' || role === 'admin';

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            {greeting} 👋
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {role === 'admin'
              ? t('dash.subtitle.admin')
              : role === 'academic_admin'
                ? t('dash.subtitle.academic')
                : role === 'teacher'
                  ? t('dash.subtitle.teacher')
                  : t('dash.subtitle.executive')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canCreate && (
            <Button
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate('/questions/new')}
            >
              {t('dash.create_new')}
            </Button>
          )}
          {(role === 'academic_admin' || role === 'admin') && (
            <Button
              variant="secondary"
              leftIcon={<CheckCircle2 className="w-4 h-4" />}
              onClick={() => navigate('/approvals')}
            >
              {t('dash.approve_questions')}
            </Button>
          )}
        </div>
      </div>

      {/* Onboarding empty state */}
      {canCreate && isNewUser ? (
        <OnboardingCards
          onCreate={() => navigate('/questions/new')}
          onImport={() => setImportOpen(true)}
          onBank={() => navigate('/question-bank')}
        />
      ) : (
        <>
          {/* Stat grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<FileText className="w-5 h-5" />}
              label={t('dash.stat.total')}
              value={(s.total_questions ?? 0).toLocaleString('th-TH')}
              tone="blue"
              loading={stats.isLoading}
            />
            <StatCard
              icon={<Clock className="w-5 h-5" />}
              label={t('dash.stat.pending')}
              value={(byStatus.pending ?? 0).toLocaleString('th-TH')}
              tone="amber"
              loading={stats.isLoading}
            />
            <StatCard
              icon={<CheckCircle2 className="w-5 h-5" />}
              label={t('dash.stat.approved')}
              value={(byStatus.approved ?? 0).toLocaleString('th-TH')}
              tone="brand"
              loading={stats.isLoading}
            />
            {role === 'admin' ? (
              <StatCard
                icon={<Users className="w-5 h-5" />}
                label={t('dash.stat.users')}
                value={(s.total_users ?? 0).toLocaleString('th-TH')}
                tone="violet"
                loading={stats.isLoading}
              />
            ) : (
              <StatCard
                icon={<TrendingUp className="w-5 h-5" />}
                label={t('dash.stat.my_questions')}
                value={(s.my_questions_count ?? 0).toLocaleString('th-TH')}
                tone="violet"
                loading={stats.isLoading}
              />
            )}
          </div>

          {/* Recent questions */}
          <Card>
            <CardHeader>
              <CardTitle>{t('dash.recent_questions')}</CardTitle>
              <button
                type="button"
                onClick={() =>
                  navigate(role === 'teacher' ? '/question-bank' : '/questions')
                }
                className="text-sm text-brand-700 hover:text-brand-800 font-medium flex items-center gap-1"
              >
                {t('dash.view_all')} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </CardHeader>
            <CardBody className="pt-0">
              {recent.isLoading ? (
                <div className="divide-y divide-slate-100">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <SkeletonRow key={i} />
                  ))}
                </div>
              ) : recent.data && recent.data.length > 0 ? (
                <ul className="divide-y divide-slate-100">
                  {recent.data.map((q) => (
                    <li
                      key={q.id}
                      className="py-3 flex items-center gap-4 cursor-pointer hover:bg-slate-50 -mx-5 px-5 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {q.question_text || t('dash.no_question_text')}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <StatusBadge status={q.status} />
                          <span className="text-xs text-slate-400">
                            {q.subject_name ?? '—'}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState
                  title={t('dash.empty_title')}
                  description={t('dash.empty_desc')}
                  action={
                    canCreate
                      ? {
                          label: t('dash.create_new'),
                          onClick: () => navigate('/questions/new'),
                        }
                      : undefined
                  }
                />
              )}
            </CardBody>
          </Card>
        </>
      )}

      <ImportQuestionsModal open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}

interface OnboardingCardsProps {
  onCreate: () => void;
  onImport: () => void;
  onBank: () => void;
}

function OnboardingCards({ onCreate, onImport, onBank }: OnboardingCardsProps) {
  const t = useT();
  const cards = [
    {
      emoji: '📝',
      title: t('onboard.create_title'),
      desc: t('onboard.create_desc'),
      tone: 'bg-brand-50 border-brand-200 hover:border-brand-400',
      iconBg: 'bg-brand-100 text-brand-700',
      onClick: onCreate,
      icon: <Plus className="w-5 h-5" />,
    },
    {
      emoji: '📥',
      title: t('onboard.import_title'),
      desc: t('onboard.import_desc'),
      tone: 'bg-blue-50 border-blue-200 hover:border-blue-400',
      iconBg: 'bg-blue-100 text-blue-700',
      onClick: onImport,
      icon: <Upload className="w-5 h-5" />,
    },
    {
      emoji: '🏛',
      title: t('onboard.bank_title'),
      desc: t('onboard.bank_desc'),
      tone: 'bg-violet-50 border-violet-200 hover:border-violet-400',
      iconBg: 'bg-violet-100 text-violet-700',
      onClick: onBank,
      icon: <Library className="w-5 h-5" />,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-brand-600 to-brand-700 p-6 text-white">
        <h2 className="text-xl font-bold leading-snug">{t('onboard.heading')}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((card) => (
          <button
            key={card.title}
            type="button"
            onClick={card.onClick}
            className={`text-left p-5 border-2 rounded-xl transition-all group ${card.tone}`}
          >
            <div className={`inline-flex w-10 h-10 rounded-xl items-center justify-center mb-3 ${card.iconBg}`}>
              {card.icon}
            </div>
            <div className="text-sm font-semibold text-slate-900 leading-snug mb-1">
              {card.emoji} {card.title}
            </div>
            <div className="text-xs text-slate-500 leading-relaxed mb-3">
              {card.desc}
            </div>
            <div className="flex items-center gap-1 text-xs font-semibold text-brand-700 group-hover:gap-2 transition-all">
              {t('onboard.go')} <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
